import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Sparkles, ArrowRight, Clock } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { PreviewQuoteDialog } from '../components/preview/PreviewQuoteDialog';
import { brandLabel } from '../lib/brandNames';
import { brandName } from '../hooks/useCompanyBrand';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (v, w) => (v.image_url ? `${BACKEND}${v.image_url}${w ? `?w=${w}` : ''}` : v.external_url);

const Lightbox = ({ items, index, onClose, onStep }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') onStep(1); if (e.key === 'ArrowLeft') onStep(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);
  const v = items[index];
  return (
    <div className="fixed inset-0 z-50 bg-neutral-950/95 flex flex-col" data-testid="preview-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-5 py-3 text-white/80 text-sm">
        <span>{v.room_type ? `${v.room_type}${v.style ? ` · ${v.style}` : ''}` : v.title}</span>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="preview-lightbox-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-12 min-h-0" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => onStep(-1)} className="absolute left-3 p-3 rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="w-5 h-5" /></button>
        <img src={img(v)} alt={v.title} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" />
        <button type="button" onClick={() => onStep(1)} className="absolute right-3 p-3 rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <p className="text-center text-white/60 text-xs py-3">{index + 1} / {items.length}</p>
    </div>
  );
};

export default function PreviewSharePage() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);
  const [lb, setLb] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  useEffect(() => {
    axios.get(`${BACKEND}/api/preview-shares/${token}`).then((r) => setShare(r.data)).catch((e) => setError(e.response?.data?.detail || 'This link is not available'));
  }, [token]);

  const items = share?.items || [];
  const swatches = [...new Map(items.filter((v) => v.color_id).map((v) => [v.color_id, v])).values()];
  return (
    <div className="min-h-screen bg-stone-50 text-neutral-900">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-28 pb-20" data-testid="preview-share-page">
        {error ? (
          <div className="py-24 text-center" data-testid="preview-share-error">
            <Clock className="w-10 h-10 mx-auto mb-4 text-neutral-300" />
            <h1 className="font-serif text-2xl">{error}</h1>
            <p className="text-sm text-neutral-500 mt-2">Ask your {brandName()} consultant for a fresh link, or browse our fabrics.</p>
            <Link to="/fabrics" className="inline-flex items-center gap-2 mt-6 text-amber-700 hover:underline text-sm">Browse fabrics <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : !share ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="aspect-[4/3] rounded-xl bg-stone-200 animate-pulse" />)}</div>
        ) : (
          <>
            <header className="max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.25em] text-amber-700 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />Prepared for {share.customer_name || 'you'}</p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight" data-testid="preview-share-title">{share.title}</h1>
              {share.note && <p className="mt-4 text-base text-neutral-600 leading-relaxed" data-testid="preview-share-note">{share.note}</p>}
              {swatches.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3" data-testid="preview-share-fabrics">
                  {swatches.map((v) => (
                    <Link key={v.color_id} to={`/fabrics?view=gallery&fabric=${v.color_id}`} className="group inline-flex items-center gap-2.5 rounded-full border border-stone-200 bg-white pl-1 pr-3 py-1 hover:border-amber-400">
                      {v.swatch_url ? <img src={`${BACKEND}${v.swatch_url}?w=128`} alt="" className="w-8 h-8 rounded-full object-cover" /> : <span className="w-8 h-8 rounded-full border" style={{ backgroundColor: v.color_hex || '#ddd' }} />}
                      <span className="text-sm"><span className="font-medium">{v.color_name || v.fabric_color}</span>{v.color_code && <span className="text-neutral-400 font-mono text-xs ml-1.5">{v.color_code}</span>}</span>
                      <span className="text-[11px] text-neutral-400 hidden sm:inline">{[brandLabel(v.brand_name), v.collection_name, v.material_name].filter(Boolean).join(' › ')}</span>
                    </Link>
                  ))}
                </div>
              )}
            </header>
            <section className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="preview-share-grid">
              {items.map((v, i) => (
                <button key={v.id} type="button" onClick={() => setLb(i)} className="group text-left rounded-2xl overflow-hidden bg-white border border-stone-200 hover:shadow-xl transition-shadow" data-testid={`preview-share-item-${v.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-stone-100"><img src={img(v, 768)} alt={v.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /></div>
                  <div className="px-4 py-3 flex items-center gap-2">
                    {v.color_hex && <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: v.color_hex }} />}
                    <span className="text-sm font-medium truncate">{v.color_name || v.fabric_color || v.title}</span>
                    {v.room_type && <span className="ml-auto text-[11px] text-neutral-500 capitalize">{v.room_type}</span>}
                  </div>
                </button>
              ))}
            </section>
            <section className="mt-14 rounded-3xl bg-neutral-900 text-white px-8 py-10 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <h2 className="font-serif text-2xl">Like what you see?</h2>
                <p className="text-sm text-neutral-300 mt-2">Request a quote and we'll measure, build and install your shades — or order free fabric samples first.</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setQuoteOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-neutral-900 px-5 py-2.5 text-sm font-medium hover:bg-amber-400" data-testid="preview-share-quote">Request a quote <ArrowRight className="w-4 h-4" /></button>
                <Link to="/request-samples" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm hover:bg-white/10" data-testid="preview-share-samples">Free samples</Link>
              </div>
            </section>
            {share.expires_at && <p className="mt-6 text-[11px] text-neutral-400 text-center">This page is available until {new Date(share.expires_at).toLocaleDateString()}.</p>}
          </>
        )}
      </main>
      <Footer />
      {quoteOpen && share && <PreviewQuoteDialog token={token} share={share} onClose={() => setQuoteOpen(false)} />}
      {lb !== null && <Lightbox items={items} index={lb} onClose={() => setLb(null)} onStep={(d) => setLb((i) => (i + d + items.length) % items.length)} />}
    </div>
  );
}
